import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ListMeta } from '@/lib/api'
import { useApi } from '@/lib/api-context'

/**
 * A conta da própria pessoa (docs/areas/autenticacao.md, "Conta"): o histórico de acessos
 * (`GET /me/sessions`, RN-AUT-25), os aplicativos MCP conectados (`GET` e `DELETE /me/connections`,
 * RN-AUT-24) e, para a gestora, os acessos de uma conta da clínica (`GET /admin/sessions?userId=`,
 * P-AUT-16). A troca de senha usa `useChangePassword` de `features/auth/api`. Os nomes de campo são
 * os de `apps/api/supabase/functions/me/schemas.ts`, `admin/sessions/routes.ts` e
 * `_shared/auth/oauth.ts`.
 */

export type DeviceType = 'desktop' | 'mobile' | 'tablet' | 'mcp' | 'unknown'

/** O dispositivo lido do user agent: descrição, não prova (`_shared/utils/device.ts`). */
export interface Device {
  description: string
  type: DeviceType
  browser: string | null
  os: string | null
}

/** Um acesso da própria pessoa, como `GET /me/sessions` devolve. */
export interface UserSession {
  sessionId: string
  startedAt: string
  /**
   * `password` (senha), `otp` ou `magiclink` (código por e-mail),
   * `oauth_provider/authorization_code` (conexão MCP); `null` se o hook não registrou.
   */
  authMethod: string | null
  channel: 'app' | 'mcp'
  /** Só em `channel = mcp`: o aplicativo que fez a conexão. */
  oauthClient: { id: string; name: string | null } | null
  userAgent: string | null
  device: Device
  ip: string | null
  lastIp: string | null
  /** A última renovação do token. */
  lastActiveAt: string
  /** Sair, senha nova, bloqueio ou desconexão; `null` enquanto o acesso não terminou. */
  endedAt: string | null
  /** Não encerrado e dentro da caixa de 12 horas. */
  isActive: boolean
  /** O acesso desta requisição. */
  isCurrent: boolean
  clinic: { id: string; name: string } | null
}

/**
 * Um acesso visto pela gestora (`GET /admin/sessions`): sem `isCurrent` e `clinic`, com a pessoa.
 * `ip` e `lastIp` vêm `null` para quem não é root (P-AUT-16).
 */
export interface ClinicSession extends Omit<UserSession, 'isCurrent' | 'clinic'> {
  user: { id: string; name: string; email: string }
  /** O login abriu a clínica ativa. A tela não mostra (RN-AUT-17). */
  isInActiveClinic: boolean
}

/** O que as duas listas têm em comum: o que a linha de acesso mostra. */
export type SessionLike = Omit<UserSession, 'isCurrent' | 'clinic'> &
  Partial<Pick<UserSession, 'isCurrent' | 'clinic'>>

/** Um aplicativo MCP autorizado, como `GET /me/connections` devolve. */
export interface Connection {
  oauthClient: { id: string; name: string; website: string | null; logo: string | null }
  scopes: string[]
  authorizedAt: string
}

export const accountKeys = {
  all: ['account'] as const,
  allSessions: ['account', 'sessions'] as const,
  sessions: (page: number) => ['account', 'sessions', page] as const,
  connections: ['account', 'connections'] as const,
}

/** Debaixo de `['users']`: resetar a senha ou reenviar o convite refaz também os acessos. */
export const userSessionKeys = {
  list: (userId: string, page: number) => ['users', 'sessions', userId, page] as const,
}

const SESSIONS_PER_PAGE = 25
const USER_SESSIONS_PER_PAGE = 10

/** Os próprios acessos, mais recentes primeiro (RN-AUT-25). */
export function useSessions(page: number) {
  const api = useApi()
  return useQuery({
    queryKey: accountKeys.sessions(page),
    queryFn: () =>
      api.list<UserSession, ListMeta>('/me/sessions', { page, perPage: SESSIONS_PER_PAGE }),
    placeholderData: keepPreviousData,
  })
}

/** Os aplicativos que a pessoa autorizou (RN-AUT-24). */
export function useConnections() {
  const api = useApi()
  return useQuery({
    queryKey: accountKeys.connections,
    queryFn: () => api.get<Connection[]>('/me/connections'),
  })
}

/**
 * Desconecta um aplicativo: o Supabase Auth apaga os tokens e as sessões dele na hora. A lista é
 * refeita mesmo quando a API responde 404 (a conexão já tinha sido desfeita em outro lugar).
 */
export function useDisconnect() {
  const api = useApi()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (oauthClientId: string) =>
      api.delete(`/me/connections/${encodeURIComponent(oauthClientId)}`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: accountKeys.connections })
      return queryClient.invalidateQueries({ queryKey: accountKeys.allSessions })
    },
  })
}

/**
 * Os acessos de uma conta da clínica, para o bloco "Acessos" da edição do usuário (P-AUT-28).
 * `userId` é a pessoa (`user.userId`), não o vínculo.
 */
export function useUserSessions(userId: string | undefined, page: number) {
  const api = useApi()
  return useQuery({
    queryKey: userSessionKeys.list(userId ?? '', page),
    queryFn: () =>
      api.list<ClinicSession, ListMeta>('/admin/sessions', {
        userId,
        page,
        perPage: USER_SESSIONS_PER_PAGE,
      }),
    enabled: Boolean(userId),
    placeholderData: keepPreviousData,
  })
}
